import { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { downloadProjectsCsv, fetchProjectSubjects, generateRemoteUrl } from "./Projects.function";
import "./Projects.scss";
import ProjectCreationModal from "./components/CreationModal/CreationModal";

const Projects = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [noFutureMeeting, setNoFutureMeeting] = useState(false);
  const [selectedOverseers, setSelectedOverseers] = useState<number[]>(
    searchParams.get("mentors")?.split(",")?.map(Number) || []
  );
  const [projectSubjects, setProjectSubjects] = useState<any[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<number[]>(
    searchParams.get("subjects")?.split(",")?.map(Number) || []
  );
  const [isSubjectsLoading, setIsSubjectsLoading] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<string | null>(null);
  const [tempEndDate, setTempEndDate] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [dateError, setDateError] = useState(false);
  const [showModalCreation, setShowModalCreation] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // const [selectedFormats, setSelectedFormats] = useState<string[]>([]);

  useEffect(() => {
    fetchProjectSubjects(setProjectSubjects, language, setIsSubjectsLoading);
  }, [language]);

  useEffect(() => {
    const params: Record<string, string> = {};

    if (selectedOverseers.length > 0) {
      params.mentors = selectedOverseers.join(",");
    }
    if (selectedSubjects.length > 0) {
      params.subjects = selectedSubjects.join(",");
    }
    setSearchParams(params);
  }, [selectedOverseers, selectedSubjects, setSearchParams]);

  useEffect(() => {
    setSelectedOverseers(searchParams.get("mentors")?.split(",")?.map(Number) || []);
    setSelectedSubjects(searchParams.get("subjects")?.split(",")?.map(Number) || []);
  }, [searchParams]);

  // const projectFormatTags = Object.values(ProjectFormats).map((format) => ({
  //   display: translate(language, `PAGES.PROJECTS.PROJECT_FORMATS.${format.toUpperCase()}`),
  //   value: format,
  // }));

  const _projectSubjectTags = useMemo(
    () =>
      projectSubjects.map((subject: any) => ({
        value: subject.id,
        display: subject.short_name,
      })),
    [projectSubjects]
  );

  const columns = [
    {
      name: translate(language, "GLOBAL.ID"),
      key: "id",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.NAME"),
      key: "name",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.START_DATE"),
      key: "dt_start",
      sortable: true,
      render: (item: any) => <span>{formatDateHumanly(language, item.dt_start, true)}</span>,
    },
    {
      name: translate(language, "GLOBAL.END_DATE"),
      key: "dt_end",
      sortable: true,
      render: (item: any) => <span>{formatDateHumanly(language, item.dt_end, true)}</span>,
    },
    {
      name: translate(language, "PAGES.PROJECTS.SUBJECT"),
      key: "subject",
      sortable: true,
      render: (item: any) => <span>{item.subject.name}</span>,
    },
    {
      name: translate(language, "PAGES.PROJECTS.OVERSEER"),
      key: "overseer",
      render: (item: any) => (
        <div>
          {item.overseer ? (
            <span
              className="text-primary clickable"
              onClick={(event) => completeNavigate(event, navigate, `/user/${item.overseer.id}`)}
            >
              {item.overseer.full_name}
            </span>
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.PROJECTS.NEXT_MEETING"),
      key: "next_meeting_dt_start",
      render: (item: any) => (
        <div>
          {item.next_meeting_dt_start ? (
            <span>{formatDateHumanly(language, item.next_meeting_dt_start, true)}</span>
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </div>
      ),
    },
    {
      name: translate(language, "GLOBAL.ACTIVE"),
      key: "is_active",
      render: (item: any) => {
        const now = new Date();
        return (
          <div className="text-center">
            {now >= new Date(item.dt_start) && now <= new Date(item.dt_end) ? (
              <span className="dst-icon-check green-font" />
            ) : (
              <span className="dst-icon-x dark-red-font" />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-3">
      <Row className="d-flex mb-3 align-items-center gap-3">
        <Col xl className="d-flex align-items-center">
          <h1 className="mb-0 me-3">{translate(language, "PAGES.PROJECTS.TITLE")}</h1>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </Col>
        <Col xl={8} className="d-flex flex-wrap gap-2 align-items-center justify-content-xl-end">
          <DstButton
            btnClass="h-100 d-flex align-items-center me-2"
            btnImageBefore={<Form.Check type="switch" className="check-box s-bold me-2" checked={onlyActive} />}
            value={translate(language, "PAGES.PROJECTS.FILTERS.ONLY_ACTIVE")}
            variant="light"
            btnHeight="50px"
            btnWidth="200px"
            clickFunction={() => setOnlyActive(!onlyActive)}
          />
          <DstButton
            btnClass="h-100 d-flex align-items-center me-2"
            btnImageBefore={
              <Form.Check
                type="switch"
                className="check-box s-bold me-2"
                checked={noFutureMeeting}
                onChange={() => setNoFutureMeeting(!noFutureMeeting)}
              />
            }
            value={translate(language, "PAGES.PROJECTS.FILTERS.NO_NEXT_MEETING")}
            variant="light"
            btnHeight="50px"
            btnWidth="200px"
            clickFunction={() => setNoFutureMeeting(!noFutureMeeting)}
          />

          <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/projects/download" }]}>
            <DstButton
              variant="light"
              value={translate(language, "PAGES.PROJECTS.BUTTON.DOWNLOAD")}
              btnImageBefore={<span className="me-2 dst-icon-download-01" />}
              clickFunction={() =>
                downloadProjectsCsv(
                  language,
                  generateRemoteUrl(
                    searchQuery,
                    selectedSubjects,
                    selectedOverseers,
                    noFutureMeeting,
                    onlyActive,
                    startDate,
                    endDate,
                    true
                  ),
                  setDownloading
                )
              }
              loading={downloading}
            />
          </PermissionComponent>

          <DstButton
            value={translate(language, "PAGES.PROJECTS.BUTTON.CREATE_PROJECT")}
            btnImageBefore={<span className="me-2 dst-icon-plus" />}
            clickFunction={() => setShowModalCreation(true)}
          />
          <ProjectCreationModal
            language={language}
            setShowCreationModal={setShowModalCreation}
            showCreationModal={showModalCreation}
          />
        </Col>
      </Row>
      <Row className="mb-3 align-items-end">
        <Col lg={6} className="d-flex flex-wrap align-items-center mb-2 gap-2">
          {/* <MultiFilterDropdown
            inputTitle={translate(language, "PAGES.PROJECTS.FILTERS.FORMATS")}
            tags={projectFormatTags}
            defaultValue={selectedFormats}
            onSelectTags={(values) => setSelectedFormats(values as string[])}
            showSearchBar
            buttonWidth="200px"
            language={language}
          /> */}
          <MultiFilterDropdown
            inputTitle={translate(language, "PAGES.PROJECTS.FILTERS.SUBJECTS")}
            tags={_projectSubjectTags}
            defaultValue={selectedSubjects}
            onSelectTags={(values) => setSelectedSubjects(values as number[])}
            showSearchBar
            buttonWidth="200px"
            language={language}
            mock={isSubjectsLoading}
          />
          <MultiSelectLiveDropdown
            category={["users"]}
            label={
              selectedOverseers?.length > 0
                ? `${selectedOverseers.length} ${translate(
                    language,
                    "PAGES.PROJECTS.FILTERS.SELECTED_OVERSEERS"
                  ).toLowerCase()}`
                : translate(language, "PAGES.PROJECTS.FILTERS.SELECTED_OVERSEERS")
            }
            language={language}
            mockClass="rounded-0 rounded-end"
            toggleClass="s-bold text-start d-flex border h-50px"
            onConfirm={(selectedUsers: any[]) => setSelectedOverseers(selectedUsers.map((user) => user.value.id))}
            showFilterButton={false}
            clearItemsOnSend={false}
          />
        </Col>
        <Col className="d-flex align-items-end justify-content-lg-end mb-2">
          <div className="w-fit me-2">
            <label htmlFor="start-date" className="form-label dark-gray-font xs-regular mb-1">
              {translate(language, "GLOBAL.START_DATE")}
            </label>
            <input
              type="date"
              id="start-date"
              className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
              value={tempStartDate || ""}
              onChange={(event) => setTempStartDate(event.target.value)}
            />
          </div>
          <div className="w-fit me-2">
            <label htmlFor="end-date" className="form-label dark-gray-font xs-regular mb-1">
              {translate(language, "GLOBAL.END_DATE")}
            </label>
            <input
              type="date"
              id="end-date"
              className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
              value={tempEndDate || ""}
              onChange={(event) => setTempEndDate(event.target.value)}
            />
          </div>
          <DstButton
            value=""
            btnClass="py-2"
            btnImageBefore={<span className="dst-icon-search-md" />}
            clickFunction={() => {
              if (tempStartDate && tempEndDate && new Date(tempEndDate) < new Date(tempStartDate)) {
                setDateError(true);
                return;
              }
              setDateError(false);
              setStartDate(tempStartDate);
              setEndDate(tempEndDate);
            }}
          />
          <DstButton
            value=""
            btnClass="py-2 ms-2"
            variant="secondary"
            btnImageBefore={<span className="dst-icon-eraser" />}
            clickFunction={() => {
              setTempStartDate(null);
              setTempEndDate(null);
              setStartDate(null);
              setEndDate(null);
              setDateError(false);
            }}
          />
        </Col>
      </Row>
      <DstLiveTable
        classTable="text-nowrap"
        remote={generateRemoteUrl(
          searchQuery,
          selectedSubjects,
          selectedOverseers,
          noFutureMeeting,
          onlyActive,
          startDate,
          endDate
        )}
        remoteKey="projects"
        columns={columns}
        pagination
        paginationAlwaysShowFooter
        paginationResizeOptions={[25, 50, 100]}
        itemsPerPage={50}
        striped
        defaultSortOrder="asc"
        onRowClick={(event, item) => completeNavigate(event, navigate, `/project/${item.id}`)}
      />
    </div>
  );
};

export default Projects;
